import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";


const slides = [
  {
    image:
      "https://res.cloudinary.com/dk5mfu099/image/upload/v1763371406/wmremove-transformed_3_ihqb6v.jpg",
    tag: "Smart Exam Prep",
    tagColor: "#0ea5e9",
    title: "Prepare Smarter.",
    titleAccent: "Succeed Faster.",
    subtitle:
      "Access thousands of curated past questions, instant answers, and powerful study tools designed to help you learn smarter and score higher.",
    accentGradient: "linear-gradient(135deg, #0288D1, #0ea5e9)",
    glowColor: "rgba(2,136,209,0.5)",
  },
  {
    image:
      "https://res.cloudinary.com/dk5mfu099/image/upload/v1763977126/wmremove-transformed_4_jaoets.jpg",
    tag: "Deep Practice Mode",
    tagColor: "#7c3aed",
    title: "Master Every Question.",
    titleAccent: "Ace Every Exam.",
    subtitle:
      "Structured revision guides, deep subject insights, and real exam-mode practice — everything you need to perform at your best.",
    accentGradient: "linear-gradient(135deg, #7c3aed, #a855f7)",
    glowColor: "rgba(124,58,237,0.5)",
  },
  {
    image:
      "https://res.cloudinary.com/dk5mfu099/image/upload/v1764088264/wmremove-transformed_5_m2g8mf.jpg",
    tag: "WAEC · NECO · UTME",
    tagColor: "#059669",
    title: "Study Anywhere.",
    titleAccent: "Improve Every Day.",
    subtitle:
      "Your personal learning companion — built for modern students preparing for the exams that matter most.",
    accentGradient: "linear-gradient(135deg, #059669, #10b981)",
    glowColor: "rgba(5,150,105,0.5)",
  },
  {
    image:
      "https://res.cloudinary.com/dk5mfu099/image/upload/v1764090261/wmremove-transformed_7_euykvl.jpg",
    tag: "Join Thousands of Students",
    tagColor: "#d97706",
    title: "Your Success",
    titleAccent: "Starts Here.",
    subtitle:
      "Thousands of students are improving their scores daily through guided learning and focused exam practice. You're next.",
    accentGradient: "linear-gradient(135deg, #d97706, #f59e0b)",
    glowColor: "rgba(217,119,6,0.5)",
  },
];

// ─── CSS keyframe injection ───────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,700;12..96,800;12..96,900&display=swap');

  @keyframes kenBurns {
    0%   { transform: scale(1.08) translate(0, 0); }
    33%  { transform: scale(1.14) translate(-1%, 0.5%); }
    66%  { transform: scale(1.12) translate(1%, -0.5%); }
    100% { transform: scale(1.18) translate(0, 1%); }
  }
  @keyframes revealUp {
    0%   { opacity: 0; transform: translateY(40px) skewY(2deg); }
    100% { opacity: 1; transform: translateY(0) skewY(0deg); }
  }
  @keyframes revealLeft {
    0%   { opacity: 0; transform: translateX(-30px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @keyframes tagReveal {
    0%   { opacity: 0; transform: translateY(12px) scale(0.92); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes lineExpand {
    0%   { width: 0; opacity: 0; }
    100% { width: 72px; opacity: 1; }
  }
  @keyframes subtitleFade {
    0%   { opacity: 0; transform: translateY(18px); filter: blur(6px); }
    100% { opacity: 1; transform: translateY(0); filter: blur(0px); }
  }
  @keyframes btnFloat {
    0%   { opacity: 0; transform: translateY(22px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes pulseDot {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 currentColor; }
    50%       { opacity: 0.5; box-shadow: 0 0 8px 2px currentColor; }
  }
  @keyframes glowPulse {
    0%, 100% { opacity: 0.6; }
    50%       { opacity: 1; }
  }
  @keyframes scanline {
    0%   { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }
  @keyframes numberReveal {
    0%   { opacity: 0; transform: translateX(20px); }
    100% { opacity: 1; transform: translateX(0); }
  }
  @keyframes progressFill {
    0%   { transform: scaleX(0); }
    100% { transform: scaleX(1); }
  }
  @keyframes orbFloat {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33%       { transform: translate(20px, -15px) scale(1.05); }
    66%       { transform: translate(-10px, 10px) scale(0.97); }
  }
  @keyframes gradientShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .slide-active .anim-tag      { animation: tagReveal    0.6s cubic-bezier(0.22,1,0.36,1) 0.05s both; }
  .slide-active .anim-title1   { animation: revealUp     0.8s cubic-bezier(0.22,1,0.36,1) 0.18s both; }
  .slide-active .anim-title2   { animation: revealUp     0.8s cubic-bezier(0.22,1,0.36,1) 0.32s both; }
  .slide-active .anim-line     { animation: lineExpand   0.7s cubic-bezier(0.22,1,0.36,1) 0.44s both; }
  .slide-active .anim-subtitle { animation: subtitleFade 0.9s cubic-bezier(0.22,1,0.36,1) 0.52s both; }
  .slide-active .anim-btns     { animation: btnFloat     0.8s cubic-bezier(0.22,1,0.36,1) 0.65s both; }
  .slide-active .anim-number   { animation: numberReveal 0.7s cubic-bezier(0.22,1,0.36,1) 0.3s both; }
`;

const Slider = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);
  const navigate = useNavigate();
  

  

  useEffect(() => {
    const t = setTimeout(() => {
      const s = swiperRef.current;
      if (s?.autoplay) {
        s.autoplay.stop();
        s.autoplay.start();
      }
    }, 300);
    const onViz = () => {
      if (!document.hidden) {
        const s = swiperRef.current;
        if (s?.autoplay) {
          s.autoplay.stop();
          s.autoplay.start();
        }
      }
    };
    document.addEventListener("visibilitychange", onViz);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onViz);
    };
  }, []);

  // const slide = slides[activeIndex];

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
        <Swiper
          onSwiper={(s) => {
            swiperRef.current = s;
          }}
          modules={[Autoplay, EffectFade]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          autoplay={{
            delay: 5500,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            waitForTransition: false,
          }}
          loop
          spaceBetween={0}
          slidesPerView={1}
          onSlideChange={(s) => setActiveIndex(s.realIndex)}
          style={{ width: "100%", height: "100%" }}
        >
          {slides.map((s, index) => (
            <SwiperSlide key={index}>
              <Box
                className={activeIndex === index ? "slide-active" : ""}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: { xs: "88vh", md: "94vh" },
                  minHeight: 560,
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                {/* Ken Burns image */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${s.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    transform: "scale(1.08)",
                    animation:
                      activeIndex === index
                        ? "kenBurns 14s ease-in-out infinite alternate"
                        : "none",
                  }}
                />

                {/* Multi-layer overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                    linear-gradient(105deg, rgba(5,14,38,0.95) 0%, rgba(5,14,38,0.72) 45%, rgba(5,14,38,0.2) 100%),
                    linear-gradient(to top, rgba(5,14,38,0.85) 0%, transparent 55%)
                  `,
                  }}
                />

                {/* Dot texture */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.035,
                    backgroundImage:
                      "radial-gradient(circle, #fff 1px, transparent 1px)",
                    backgroundSize: "28px 28px",
                  }}
                />

                {/* Scanline effect */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    height: "200px",
                    background:
                      "linear-gradient(to bottom, transparent, rgba(255,255,255,0.015), transparent)",
                    animation: "scanline 6s linear infinite",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />

                {/* Ambient glow orbs */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "-10%",
                    right: "5%",
                    width: { xs: 300, md: 500 },
                    height: { xs: 300, md: 500 },
                    borderRadius: "50%",
                    pointerEvents: "none",
                    background: `radial-gradient(circle, ${s.glowColor} 0%, transparent 70%)`,
                    opacity: 0.35,
                    animation: "orbFloat 12s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "-5%",
                    right: "25%",
                    width: { xs: 200, md: 350 },
                    height: { xs: 200, md: 350 },
                    borderRadius: "50%",
                    pointerEvents: "none",
                    background: `radial-gradient(circle, rgba(2,136,209,0.25) 0%, transparent 70%)`,
                    opacity: 0.4,
                    animation: "orbFloat 16s ease-in-out infinite reverse",
                  }}
                />

                {/* Vertical accent line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: 20, md: 40 },
                    top: "15%",
                    bottom: "15%",
                    width: 2,
                    borderRadius: 99,
                    background: `linear-gradient(to bottom, transparent, ${s.tagColor}, transparent)`,
                    opacity: 0.5,
                    zIndex: 2,
                  }}
                />

                {/* Slide content */}
                <Container
                  maxWidth="lg"
                  sx={{ position: "relative", zIndex: 3, px: { xs: 4, md: 8 } }}
                >
                  <Box sx={{ maxWidth: { xs: "100%", md: "60%" } }}>
                    {/* Tag chip */}
                    <Box
                      className="anim-tag"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1.2,
                        px: 2.2,
                        py: 0.9,
                        mb: 3.5,
                        borderRadius: "10px",
                        background: `${s.tagColor}18`,
                        border: `1px solid ${s.tagColor}40`,
                        backdropFilter: "blur(12px)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: s.tagColor,
                          color: s.tagColor,
                          animation: "pulseDot 1.8s ease-in-out infinite",
                          boxShadow: `0 0 8px ${s.tagColor}`,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          color: s.tagColor,
                          textTransform: "uppercase",
                          letterSpacing: "1.2px",
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                        }}
                      >
                        {s.tag}
                      </Typography>
                    </Box>

                    {/* Title line 1 */}
                    <Box
                      className="anim-title1"
                      sx={{ overflow: "hidden", mb: 0.5 }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: "#fff",
                          fontSize: {
                            xs: "2.4rem",
                            sm: "3.2rem",
                            md: "4.2rem",
                          },
                          lineHeight: 1.05,
                          letterSpacing: "-2px",
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                        }}
                      >
                        {s.title}
                      </Typography>
                    </Box>

                    {/* Title line 2 — gradient text */}
                    <Box
                      className="anim-title2"
                      sx={{ overflow: "hidden", mb: 3 }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: {
                            xs: "2.4rem",
                            sm: "3.2rem",
                            md: "4.2rem",
                          },
                          lineHeight: 1.05,
                          letterSpacing: "-2px",
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                          background: s.accentGradient,
                          backgroundSize: "200% 200%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: "gradientShift 4s ease infinite",
                        }}
                      >
                        {s.titleAccent}
                      </Typography>
                    </Box>

                    {/* Accent line */}
                    <Box
                      className="anim-line"
                      sx={{
                        height: 4,
                        borderRadius: 99,
                        mb: 3.5,
                        background: s.accentGradient,
                        boxShadow: `0 0 16px ${s.glowColor}`,
                        transformOrigin: "left",
                      }}
                    />

                    {/* Subtitle */}
                    <Typography
                      className="anim-subtitle"
                      sx={{
                        color: "rgba(255,255,255,0.62)",
                        fontSize: { xs: "0.98rem", md: "1.12rem" },
                        lineHeight: 1.82,
                        mb: 5,
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 400,
                        maxWidth: 520,
                      }}
                    >
                      {s.subtitle}
                    </Typography>

                    {/* CTA buttons */}
                    <Stack
                      className="anim-btns"
                      // Keep row on small screens, wrap if they get too crowded
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{
                        flexWrap: "wrap", // Crucial: allows buttons to drop to next line individually
                        justifyContent: "flex-start",
                        alignItems: "center",
                        width: "100%",
                        gap: "10px",
                      }}
                    >
                      {[
                        {
                          label: "Get Started Free",
                          path: "/login",
                          primary: true,
                          icon: <FaArrowRight size={13} />,
                        },
                        {
                          label: "Login As A Teacher",
                          path: "/teachers-login",
                          primary: false,
                        },
                        // {
                        //   label: "Examination for students",
                        //   path: "/student-login",
                        //   primary: false,
                        // },
                      ].map((btn, i) => (
                        <Button
                          key={i}
                          variant={btn.primary ? "contained" : "outlined"}
                          endIcon={btn.icon}
                          onClick={() => navigate(btn.path)}
                          className="anim-btn-child" // Target this with GSAP for a staggered entrance
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: btn.primary ? 800 : 700,
                            fontSize: "0.92rem",
                            whiteSpace: "nowrap", // Prevents text from breaking inside the button
                            minWidth: "fit-content",
                            // Adjusted padding: slightly less on mobile to prevent overflow
                            px: { xs: 3, md: 4.5 },
                            py: 1.7,
                            width: { xs: "100%", sm: "auto" }, // Full width on mobile, auto on desktop
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            ...(btn.primary
                              ? {
                                  background: s.accentGradient,
                                  backgroundSize: "200% 200%",
                                  boxShadow: `0 8px 28px ${s.glowColor}`,
                                }
                              : {
                                  borderColor: "rgba(255,255,255,0.28)",
                                  color: "#fff",
                                  backdropFilter: "blur(12px)",
                                  background: "rgba(255,255,255,0.07)",
                                }),
                            transition:
                              "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              transform: "translateY(-3px)",
                              filter: "brightness(1.1)",
                              borderColor:
                                !btn.primary && "rgba(255,255,255,0.6)",
                              background:
                                !btn.primary && "rgba(255,255,255,0.14)",
                              boxShadow:
                                btn.primary && `0 14px 36px ${s.glowColor}`,
                            },
                          }}
                        >
                          {btn.label}
                        </Button>
                      ))}
                    </Stack>
                  

                   
                  </Box>
                </Container>

                {/* Slide number — bottom right */}
                <Box
                  className="anim-number"
                  sx={{
                    position: "absolute",
                    bottom: { xs: 32, md: 44 },
                    right: { xs: 24, md: 52 },
                    zIndex: 4,
                    textAlign: "right",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: "2.8rem", md: "3.6rem" },
                      color: "rgba(255,255,255,0.12)",
                      lineHeight: 1,
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      letterSpacing: "-2px",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Box
                    sx={{
                      width: 28,
                      height: 2,
                      background: `rgba(255,255,255,0.2)`,
                      borderRadius: 99,
                      ml: "auto",
                      mt: 0.5,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.25)",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      letterSpacing: "0.5px",
                      mt: 0.4,
                    }}
                  >
                    OF {String(slides.length).padStart(2, "0")}
                  </Typography>
                </Box>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* ── Progress bar (auto-resets per slide) ── */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "rgba(255,255,255,0.08)",
            zIndex: 10,
          }}
        >
          <Box
            key={activeIndex}
            sx={{
              height: "100%",
              borderRadius: 99,
              background: slides[activeIndex].accentGradient,
              boxShadow: `0 0 10px ${slides[activeIndex].glowColor}`,
              transformOrigin: "left",
              animation: "progressFill 5.5s linear forwards",
            }}
          />
        </Box>

        {/* ── Dot indicators ── */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "absolute",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            alignItems: "center",
          }}
        >
          {slides.map((s, i) => (
            <Box
              key={i}
              onClick={() => swiperRef.current?.slideTo(i)}
              sx={{
                height: i === activeIndex ? 5 : 4,
                borderRadius: 99,
                cursor: "pointer",
                background:
                  i === activeIndex
                    ? s.accentGradient
                    : "rgba(255,255,255,0.22)",
                width: i === activeIndex ? 36 : 8,
                transition: "all 0.4s cubic-bezier(0.34,1.56,0.64,1)",
                boxShadow:
                  i === activeIndex ? `0 0 12px ${s.glowColor}` : "none",
                "&:hover": {
                  background:
                    i !== activeIndex
                      ? "rgba(255,255,255,0.5)"
                      : s.accentGradient,
                },
              }}
            />
          ))}
        </Stack>

        {/* ── Slide label row (bottom-left) ── */}
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 52, md: 56 },
            left: { xs: 20, md: 44 },
            zIndex: 10,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 1.5,
              background: "rgba(255,255,255,0.3)",
              borderRadius: 99,
            }}
          />
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              color: "rgba(255,255,255,0.4)",
              textTransform: "uppercase",
              letterSpacing: "1.4px",
              fontFamily: "'Bricolage Grotesque', sans-serif",
            }}
          >
            {slides[activeIndex].tag}
          </Typography>
        </Box>

        {/* ── Bottom fade-to-page ── */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 120,
            background: "linear-gradient(to bottom, transparent, #e0f2fe)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        />
      </Box>
    </>
  );
};

export default Slider;
